import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Star } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { BUSINESS, waLink } from "../data/siteConfig";
import { WhatsAppIcon } from "./WhatsAppIcon";

const EASE = [0.22, 1, 0.36, 1];

const Hero = () => {
  const { t, isRtl } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "10%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const eyebrowClass = isRtl
    ? "text-sm font-bold text-bronze-dark"
    : "text-xs font-semibold uppercase tracking-[0.35em] text-bronze-dark";

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 pb-20 lg:pt-44 lg:pb-28"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 end-[-10%] h-[34rem] w-[34rem] rounded-full bg-bronze/10 blur-3xl"
      />
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-12 lg:gap-10 lg:px-10">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className={`mb-8 flex items-center gap-3 ${eyebrowClass}`}
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-10 bg-bronze" aria-hidden="true" />
            {t.hero.eyebrow}
          </motion.p>

          <h1
            data-testid="hero-headline"
            className={`font-head text-5xl sm:text-6xl lg:text-7xl leading-[1.08] text-ink ${
              isRtl ? "" : "tracking-tight"
            }`}
          >
            {t.hero.headlineLines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${
                    line.accent
                      ? `text-bronze-dark ${isRtl ? "" : "italic"}`
                      : ""
                  }`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.2 + i * 0.15, ease: EASE }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-taupe"
            data-testid="hero-subcopy"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={waLink(t.hero.waMessage)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="group inline-flex items-center gap-2.5 rounded-full bg-bronze px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(184,151,104,0.35)] transition-colors duration-300 hover:bg-bronze-dark"
            >
              <WhatsAppIcon className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-12" />
              {t.hero.ctaPrimary}
            </a>
            <a
              href="#services"
              data-testid="hero-explore-cta"
              className="group inline-flex items-center gap-2 rounded-full border border-ink/15 px-7 py-3.5 text-sm font-semibold text-ink transition-colors duration-300 hover:border-bronze hover:text-bronze-dark"
            >
              {t.hero.ctaSecondary}
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
            className="mt-12 inline-flex flex-col gap-2"
            data-testid="google-rating-badge"
          >
            <div className="inline-flex w-fit items-center gap-4 rounded-2xl border border-line bg-white/80 px-5 py-4 shadow-[0_8px_30px_rgba(28,27,26,0.05)] backdrop-blur-sm">
              <span className="font-head text-3xl text-ink">{BUSINESS.google.rating}</span>
              <div>
                <div className="flex gap-0.5" aria-label={`${BUSINESS.google.rating} out of 5 stars`}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-bronze text-bronze" />
                  ))}
                </div>
                <p className="mt-1 text-sm font-medium text-ink">
                  {BUSINESS.google.rating} {t.hero.ratingTitle}
                </p>
              </div>
            </div>
            <p className="ps-1 text-xs text-taupe">{t.hero.ratingNote}</p>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
          className="relative lg:col-span-5"
        >
          <div
            aria-hidden="true"
            className="absolute -inset-4 rounded-t-[13rem] rounded-b-[2.5rem] border border-bronze/30"
          />
          <div className="relative aspect-[4/5] overflow-hidden rounded-t-[12rem] rounded-b-[2rem] shadow-[0_30px_60px_rgba(28,27,26,0.12)]">
            <motion.img
              src={BUSINESS.images.hero.src}
              alt={isRtl ? BUSINESS.images.hero.altAr : BUSINESS.images.hero.altEn}
              style={{ y: imgY }}
              className="h-[115%] w-full scale-110 object-cover"
              loading="eager"
              fetchpriority="high"
              data-testid="hero-image"
            />
          </div>
          <div className="absolute bottom-6 start-6 inline-flex items-center gap-2 rounded-full bg-white/85 px-4 py-2 text-xs font-semibold text-ink shadow-md backdrop-blur-sm" data-testid="hero-image-caption">
            <MapPin className="h-3.5 w-3.5 text-bronze-dark" />
            {t.hero.imageCaption}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
